// 투사체(이동 + 꼬리): 앞으로 날아가는 빛줄기. 머리는 밝고 둥글게, 꼬리는 가늘게 끌린다. 어느 방향에서 봐도 보이도록 가로·세로 띠 두 장을 십자로 둔다.
// 도착하면 머리가 멈추고 꼬리가 따라잡아 사라진다. 일정 속도(length / 비행 시간).
// 쓰는 칸: length = 날아가는 거리(m), width = 머리 두께(m), tail = 꼬리 길이(m), life = 비행 + 꼬리 따라잡기 전체(초),
//          y = 높이, ahead = 시작을 앞으로 옮기는 거리, color(몸통)·color2(머리 핵)·intensity·seed. 드로우콜 1.
import * as THREE from 'three/webgpu';
import { abs, clamp, mix, smoothstep, texture, uv, vec2 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { stripLayout } from './layout';
import { clamp01 } from './path';
import { MODULE_CAPS, type ModuleKind } from './types';

export const PROJECTILE_SEG = 10;
/** 머리 쪽 둥근 끝: 마지막 10%에서 폭이 줄어든다 */
const SHAPE = new Float32Array(PROJECTILE_SEG + 1);
for (let i = 0; i <= PROJECTILE_SEG; i++) {
  const f = i / PROJECTILE_SEG;
  SHAPE[i] = Math.pow(f, 0.7) * (f > 0.9 ? Math.max(0.35, 1 - (f - 0.9) * 6.5) : 1);
}

function makeMaterial(noise: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const age = ctl.x, env = ctl.y, seed = ctl.z;
  const u = uv().x, v = uv().y;
  const band = abs(v.mul(2).sub(1));
  const across = band.oneMinus().pow(1.1);
  const streak = texture(noise, vec2(u.mul(5).sub(age.mul(t2.w)), v.mul(0.5).add(seed))).g;
  const alpha = u.pow(1.2).mul(across).mul(streak.mul(0.5).add(0.6)).mul(env);
  // 머리 핵: 끝에 가까울수록 부색(밝은 핵)
  const hot = clamp(smoothstep(0.72, 1.0, u).add(band.oneMinus().pow(5).mul(0.45)), 0, 1);
  const col = mix(t1.rgb, t2.rgb, hot).mul(t1.w).mul(0.8);

  const m = new THREE.MeshBasicNodeMaterial();
  m.transparent = true;
  m.depthWrite = false;
  m.side = THREE.DoubleSide;
  m.blending = THREE.AdditiveBlending;
  m.forceSinglePass = true;
  m.fog = false;
  m.colorNode = col;
  m.opacityNode = alpha;
  return m;
}

export class ProjectileModule extends BatchModule {
  readonly kind: ModuleKind = 'projectile';

  constructor(noise: THREE.Texture, cap = MODULE_CAPS.projectile) {
    super(cap, stripLayout(PROJECTILE_SEG, 2), makeMaterial(noise), 22, 'fx-projectile');
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const sin = Math.sin(s.yaw), cos = Math.cos(s.yaw);
    const D = Math.max(0.01, s.length * s.scale);
    const tailM = Math.max(0.01, s.tail * s.scale);
    const W = s.width * s.scale;
    // 비행 시간 T: 도착(거리 D)까지. 전체 수명 = T × (1 + 꼬리/D)라 꼬리가 도착 뒤 따라잡는다
    const T = s.life / (1 + tailM / D);
    const vd = (s.age / T) * D;
    const bx = s.x + sin * s.ahead, bz = s.z + cos * s.ahead;
    const rx = cos, rz = -sin;
    const per = (PROJECTILE_SEG + 1) * 2;
    for (let i = 0; i <= PROJECTILE_SEG; i++) {
      const f = i / PROJECTILE_SEG;
      let d = vd - tailM * (1 - f);
      d = d < 0 ? 0 : d > D ? D : d;
      const px = bx + sin * d, pz = bz + cos * d, py = s.y;
      const hw = 0.5 * W * SHAPE[i];
      // 가로 띠(바닥과 평행)
      const k = o + i * 6;
      out[k] = px - rx * hw; out[k + 1] = py; out[k + 2] = pz - rz * hw;
      out[k + 3] = px + rx * hw; out[k + 4] = py; out[k + 5] = pz + rz * hw;
      // 세로 띠
      const q = o + per * 3 + i * 6;
      out[q] = px; out[q + 1] = py - hw; out[q + 2] = pz;
      out[q + 3] = px; out[q + 4] = py + hw; out[q + 5] = pz;
    }
  }

  protected ctl(s: Slot, out: Float32Array): void {
    out[0] = clamp01(s.age / s.life);
    out[1] = Math.min(1, s.age / 0.015);
    out[2] = s.seed;
    out[3] = 0;
  }

  protected override tint2w(): number { return 3; }
}
